import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Wysiwyg } from "../Wysiwyg";

const companyLogoCardVariants = cva(
  "group flex aspect-square flex-col items-center justify-center rounded-lg p-10 backdrop-blur-md transition-colors duration-300",
  {
    variants: {
      variant: {
        light: "bg-white/20 text-white hover:bg-yellow",
        dark: "bg-off-white text-black hover:bg-purple",
      },
      isActive: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "light",
        isActive: true,
        className: "bg-yellow text-black hover:bg-yellow hover:text-black",
      },
      {
        variant: "dark",
        isActive: true,
        className: "bg-purple text-white hover:bg-purple hover:text-white",
      },
    ],
    defaultVariants: {
      variant: "light",
      isActive: false,
    },
  }
);

const iconVariants = cva("w-full transition-colors duration-300", {
  variants: {
    variant: {
      light: "",
      dark: "",
    },
    isActive: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: "light",
      isActive: true,
      className: "text-black group-hover:text-black",
    },
    {
      variant: "dark",
      isActive: true,
      className: "text-white group-hover:text-white",
    },
    {
      variant: "light",
      isActive: false,
      className: "text-white group-hover:text-black",
    },
    {
      variant: "dark",
      isActive: false,
      className: "text-black group-hover:text-white",
    },
  ],
  defaultVariants: {
    variant: "light",
    isActive: false,
  },
});

export interface CompanyLogoCardProps extends VariantProps<
  typeof companyLogoCardVariants
> {
  /**
   * Name of the company
   */
  companyName: string;

  /**
   * Raw SVG string for the company logo
   */
  svg: string;

  /**
   * Whether this card is currently active/focused
   */
  isActive?: boolean;

  /**
   * Optional className for custom styling
   */
  className?: string;
}

/**
 * CompanyLogoCard component - displays a company logo
 * Features semi-transparent backgrounds with blur effect and hover states
 */
export const CompanyLogoCard: React.FC<CompanyLogoCardProps> = ({
  companyName,
  svg,
  variant = "light",
  isActive = false,
  className = "",
}) => {
  return (
    <div
      className={companyLogoCardVariants({ variant, isActive, className })}
      title-={`MUUUH! x ${companyName}`}
    >
      <div className={iconVariants({ variant, isActive })}>
        <Wysiwyg html={svg} mode="raw" />
      </div>
    </div>
  );
};
